import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import { formatMoney } from '@/lib/format';
import { PAYMENT_LABELS } from '@/lib/types';

interface TopProduct {
  name: string;
  quantity: number;
  revenue: number;
}

export default function ReportsPage() {
  const { branches, activeBranchId } = useBranch();
  const [branchId, setBranchId] = useState<string | null>(activeBranchId);
  const [totalSales, setTotalSales] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [byMethod, setByMethod] = useState<Record<string, number>>({});
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBranchId(activeBranchId);
  }, [activeBranchId]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let salesQuery = supabase.from('sales').select('id, total, payment_method');
        if (branchId) salesQuery = salesQuery.eq('branch_id', branchId);

        const salesRes = await salesQuery;
        if (salesRes.error) throw salesRes.error;
        if (!active) return;

        const sales = salesRes.data ?? [];
        const totalSales = sales.reduce((s, r) => s + Number(r.total || 0), 0);
        const byMethod: Record<string, number> = {};
        for (const s of sales) {
          byMethod[s.payment_method] = (byMethod[s.payment_method] || 0) + Number(s.total || 0);
        }

        // Productos más vendidos (filtrando por sucursal vía sale_id)
        let itemsQuery = supabase.from('sale_items').select('product_name, quantity, subtotal, sale_id');
        if (branchId) {
          const saleIds = sales.map((s) => s.id);
          if (saleIds.length > 0) {
            itemsQuery = itemsQuery.in('sale_id', saleIds);
          } else {
            itemsQuery = itemsQuery.eq('sale_id', '00000000-0000-0000-0000-000000000000');
          }
        }
        const itemsRes = await itemsQuery;
        if (itemsRes.error) throw itemsRes.error;
        if (!active) return;

        const items = itemsRes.data ?? [];
        const map = new Map<string, TopProduct>();
        for (const it of items) {
          const prev = map.get(it.product_name) ?? { name: it.product_name, quantity: 0, revenue: 0 };
          prev.quantity += Number(it.quantity || 0);
          prev.revenue += Number(it.subtotal || 0);
          map.set(it.product_name, prev);
        }
        const top = Array.from(map.values()).sort((a, b) => b.quantity - a.quantity).slice(0, 10);

        setTotalSales(totalSales);
        setInvoiceCount(sales.length);
        setByMethod(byMethod);
        setTopProducts(top);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'No se pudieron cargar los reportes');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [branchId]);

  const average = invoiceCount > 0 ? totalSales / invoiceCount : 0;

  const statCards = [
    { label: 'Ventas totales', value: formatMoney(totalSales), icon: 'ri-money-dollar-circle-line', color: 'bg-primary-100 text-primary-700' },
    { label: 'Facturas emitidas', value: String(invoiceCount), icon: 'ri-receipt-line', color: 'bg-accent-100 text-accent-700' },
    { label: 'Ticket promedio', value: formatMoney(average), icon: 'ri-calculator-line', color: 'bg-secondary-100 text-secondary-900' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Reportes</h1>
          <p className="mt-1 text-foreground-600">Resumen de ventas y productos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/reports/ganancias"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-3 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-funds-line" /> Ver ganancias
          </Link>
          <select
          value={branchId ?? ''}
          onChange={(e) => setBranchId(e.target.value || null)}
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          <option value="">Todas las sucursales</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {statCards.map((card) => (
              <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${card.color}`}>
                  <i className={`${card.icon} text-xl`} />
                </div>
                <p className="mt-3 text-2xl font-heading font-bold text-foreground-950 whitespace-nowrap">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-foreground-600">{card.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-foreground-950">Productos más vendidos</h2>
              {topProducts.length === 0 ? (
                <p className="mt-4 text-sm text-foreground-600">Sin datos de ventas todavía.</p>
              ) : (
                <ul className="mt-4 divide-y divide-background-100">
                  {topProducts.map((p, i) => (
                    <li key={p.name} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold flex items-center justify-center shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm text-foreground-900">{p.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-foreground-950">{p.quantity} uds.</span>
                        <span className="block text-xs text-foreground-500">{formatMoney(p.revenue)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-foreground-950">Ventas por método de pago</h2>
              {Object.keys(byMethod).length === 0 ? (
                <p className="mt-4 text-sm text-foreground-600">Sin datos de pagos todavía.</p>
              ) : (
                <ul className="mt-4 divide-y divide-background-100">
                  {Object.entries(byMethod).map(([method, amount]) => (
                    <li key={method} className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-foreground-900">
                        {PAYMENT_LABELS[method] || method}
                      </span>
                      <span className="text-sm font-semibold text-foreground-950">{formatMoney(amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}